import { Tier, Quality } from 'dragonmouse-shared/interfaces';
import {
	LOW_THRESHOLD,
	MAX_BONUS_REFILLS,
	REFILL_POLICIES,
	Wallet,
} from 'dragonmouse-shared/economy';
import { showConfirmModal, showGoldRefillModal, setChatQuality } from 'redux/mode/actions';
import { selectUser, selectWalletLevels } from 'redux/db/reducers';
import store from 'redux/store';
import { navigate } from 'utils';
import { profile } from 'routes';
import { trackEconomyAction } from 'analytics';

export type CurrencyLevel = 'EMPTY' | 'LOW' | 'AMPLE';

export interface WalletLevels {
	goldLevel: CurrencyLevel;
	silverLevel: CurrencyLevel;
}

// AMPLE: Has coins AND (coins > LOW_THRESHOLD OR refill is available)
// LOW: Has coins but will run out before refill
// EMPTY: No coins
export function computeWalletLevels(wallet: Wallet, tier: Tier): WalletLevels {
	const now = new Date();
	const policy = REFILL_POLICIES[tier];

	const computeLevel = (
		balance: number,
		refillDate: Date | null,
		eraHours: number
	): CurrencyLevel => {
		if (balance > LOW_THRESHOLD) return 'AMPLE';

		// If the user is due for a refill, consider their supply AMPLE
		if (refillDate) {
			const eraMs = eraHours * 60 * 60 * 1000;
			const earliestPossibleRefill = new Date(refillDate.getTime() + eraMs);
			if (earliestPossibleRefill < now) {
				return 'AMPLE';
			}
		}

		if (balance > 0) return 'LOW';
		return 'EMPTY';
	};

	return {
		goldLevel:
			// tier 2 has infinite gold
			tier === Tier.TIER_2
				? 'AMPLE'
				: computeLevel(wallet.gold, wallet.goldRefill, policy.goldEraHours),
		silverLevel:
			// tier 1 and 2 have infinite silver
			tier === Tier.TIER_1 || tier === Tier.TIER_2
				? 'AMPLE'
				: computeLevel(wallet.silver, wallet.silverRefill, policy.silverEraHours),
	};
}

/**
 * Intercepts submission if wallet is empty based on tier and quality.
 * Queries Redux store directly for subscription, chatQuality, walletLevels, and refill availability.
 * Shows appropriate modals (upgrade, refill) if needed.
 * @returns true if submission was intercepted (blocked), false if submission can proceed
 */
export function interceptSubmissionIfWalletEmpty(): boolean {
	const state = store.getState();
	const subscription = state.db.subscription;
	const chatQuality = state.mode.chatQuality;
	const walletLevels = selectWalletLevels(state.db);
	const user = selectUser(state.db);
	const bonusRefillCount = user?.metadata?.bonusRefillCount ?? 0;
	const hasRefillsAvailable = bonusRefillCount < MAX_BONUS_REFILLS;

	switch (subscription?.tier ?? Tier.FREE) {
		case Tier.FREE:
			if (chatQuality === Quality.SILVER && walletLevels?.silverLevel === 'EMPTY') {
				// they have gold, they can upgrade or switch to gold
				if (walletLevels?.goldLevel !== 'EMPTY') {
					store.dispatch(
						showConfirmModal({
							title: "You're out of silver",
							description:
								'Silver refills tomorrow — upgrade for more or switch to gold to keep creating without waiting.',
							submitLabel: 'Upgrade',
							altActionLabel: 'Switch to Gold',
							onSubmit: () => {
								trackEconomyAction('upgrade_clicked');
								navigate(profile());
							},
							onAltAction: () => {
								store.dispatch(setChatQuality(Quality.GOLD));
							},
						})
					);
					return true;
				}
				store.dispatch(
					showGoldRefillModal({
						title: "You're out of silver",
						refillsAvailable: hasRefillsAvailable,
					})
				);
				return true;
			} else if (chatQuality === Quality.GOLD && walletLevels?.goldLevel === 'EMPTY') {
				store.dispatch(showGoldRefillModal({ refillsAvailable: hasRefillsAvailable }));
				return true;
			}
			break;
		case Tier.TIER_1:
			if (chatQuality === Quality.GOLD && walletLevels?.goldLevel === 'EMPTY') {
				store.dispatch(showGoldRefillModal({ refillsAvailable: hasRefillsAvailable }));
				return true;
			}
			break;
	}
	return false;
}
